import {type Icon, RepoPushIcon, SortAscIcon, SortDescIcon, StarIcon, TypographyIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'

interface OrderOptions {
  label: string
  defaultDirection: 'asc' | 'desc'
  icon: Icon
}

const sortingOptions: Record<string, OrderOptions> = {
  updated: {label: 'Last pushed', defaultDirection: 'desc', icon: RepoPushIcon},
  name: {label: 'Name', defaultDirection: 'asc', icon: TypographyIcon},
  stars: {label: 'Stars', defaultDirection: 'desc', icon: StarIcon},
}

export function SortingDropdown({
  sortingItemSelected,
  onSortingItemSelect,
}: {
  sortingItemSelected: string
  onSortingItemSelect: (key: string) => void
}) {
  const selectedKey = sortingItemSelected || 'updated'
  const [field, direction] = selectedKey.split('-')
  const isAscending = direction === 'asc'
  const orderIcon = isAscending ? SortAscIcon : SortDescIcon

  function select(newField: string, newDirection: 'asc' | 'desc') {
    if (newDirection === 'asc') {
      onSortingItemSelect(`${newField}-asc`)
    } else if (newField === 'updated') {
      onSortingItemSelect('')
    } else {
      onSortingItemSelect(newField)
    }
  }

  const options = sortingOptions[field!] || sortingOptions.updated!

  return (
    <ActionMenu>
      <ActionMenu.Button leadingVisual={orderIcon} sx={{color: 'fg.muted'}} variant="invisible">
        {options.label}
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          {Object.entries(sortingOptions).map(([key, {label, defaultDirection, icon: FieldIcon}]) => (
            <ActionList.Item key={key} selected={key === field} onSelect={() => select(key, defaultDirection)}>
              <ActionList.LeadingVisual>
                <FieldIcon />
              </ActionList.LeadingVisual>
              {label}
            </ActionList.Item>
          ))}
          <ActionList.Divider />
          <ActionList.Item key="ascending" selected={isAscending} onSelect={() => select(field!, 'asc')}>
            <ActionList.LeadingVisual>
              <SortAscIcon />
            </ActionList.LeadingVisual>
            Ascending
          </ActionList.Item>
          <ActionList.Item key="descending" selected={!isAscending} onSelect={() => select(field!, 'desc')}>
            <ActionList.LeadingVisual>
              <SortDescIcon />
            </ActionList.LeadingVisual>
            Descending
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ SortingDropdown.displayName ||= 'SortingDropdown' } catch {}